import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, Loader2, ShieldCheck, CalendarPlus, ImageIcon, LayoutGrid } from "lucide-react";

const REDIRECT = `${window.location.origin}/api/portal/callback`;

const TILE_META = [
  { key: "courses", label: "My Courses" },
  { key: "checkins", label: "Check-Ins" },
  { key: "resources", label: "Resources" },
  { key: "packages", label: "Packages" },
];

async function compressImage(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  try {
    const url = URL.createObjectURL(file);
    const img = document.createElement("img");
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const max = 1200;
    let { width, height } = img;
    if (width > max || height > max) {
      const r = Math.min(max / width, max / height);
      width = Math.round(width * r); height = Math.round(height * r);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    canvas.getContext("2d").drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch (e) { return file; }
}

export default function SsoSetup() {
  const [clientKey, setClientKey] = useState("6a87f7636a8e4c1cf964214b-mt2uhwsb");
  const [secret, setSecret] = useState("");
  const [configured, setConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  const [savingBooking, setSavingBooking] = useState(false);
  const [pricingUrl, setPricingUrl] = useState("");
  const [savingPricing, setSavingPricing] = useState(false);
  const [tiles, setTiles] = useState({});
  const [tileFiles, setTileFiles] = useState({});
  const [savingTiles, setSavingTiles] = useState(false);

  useEffect(() => {
    api.get("/portal/sso-config").then(({ data }) => {
      if (data.client_key) setClientKey(data.client_key);
      setConfigured(data.configured);
    }).catch(() => {});
    api.get("/settings/booking").then(({ data }) => setBookingUrl(data.url || "")).catch(() => {});
    api.get("/settings/pricing").then(({ data }) => setPricingUrl(data.url || "")).catch(() => {});
    api.get("/settings/tiles").then(({ data }) => setTiles(data.tiles || {})).catch(() => {});
  }, []);

  const saveTiles = async () => {
    setSavingTiles(true);
    try {
      const fd = new FormData();
      for (const t of TILE_META) {
        if (tileFiles[t.key]) fd.append(t.key, await compressImage(tileFiles[t.key]));
      }
      const { data } = await api.post("/settings/tiles", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTiles(data.tiles || {});
      setTileFiles({});
      toast.success("Dashboard tile images saved");
    } catch (e) { toast.error("Could not save tile images"); }
    finally { setSavingTiles(false); }
  };

  const saveBooking = async () => {
    setSavingBooking(true);
    try {
      await api.post("/settings/booking", { url: bookingUrl });
      toast.success("Booking widget link saved");
    } catch (e) { toast.error("Could not save booking link"); }
    finally { setSavingBooking(false); }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await api.post("/settings/pricing", { url: pricingUrl });
      toast.success("Pricing table link saved");
    } catch (e) { toast.error("Could not save pricing link"); }
    finally { setSavingPricing(false); }
  };

  const save = async () => {
    if (!clientKey.trim() || !secret.trim()) { toast.error("Enter both the Client ID and Secret"); return; }
    setSaving(true);
    try {
      await api.post("/portal/sso-config", { client_key: clientKey, shared_secret: secret, redirect_url: REDIRECT });
      toast.success("Client Portal SSO credentials saved securely");
      setConfigured(true); setSecret("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="sso-setup-page">
      <PageHeader title="Client Portal (SSO)" subtitle="Securely store your GHL app Client ID and Secret to enable single sign-on for the client dashboard." />
      <Card className="max-w-xl rounded-2xl border-navy-900/5 bg-white p-8 shadow-ambient">
        {configured && (
          <div className="mb-4 flex items-center gap-2 text-emerald-600" data-testid="sso-configured">
            <CheckCircle2 className="h-4 w-4" /> Secret saved (encrypted)
          </div>
        )}
        <div className="space-y-4">
          <div>
            <Label>Client ID (Client Key)</Label>
            <Input data-testid="sso-client-key" value={clientKey} onChange={(e) => setClientKey(e.target.value)} className="mt-1 font-mono focus-visible:ring-gold-500" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Client Secret</Label>
            <Input type="password" data-testid="sso-secret" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={configured ? "•••••••• (enter to replace)" : "Paste your GHL app secret"} className="mt-1 font-mono focus-visible:ring-gold-500" />
          </div>
          <div>
            <Label>Redirect URL (already set in GHL)</Label>
            <Input readOnly value={REDIRECT} className="mt-1 font-mono text-xs text-navy-400" />
          </div>
          <Button data-testid="sso-save" onClick={save} disabled={saving} className="rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save securely
          </Button>
        </div>
        <p className="mt-4 flex items-center gap-1.5 text-xs text-navy-400">
          <ShieldCheck className="h-3.5 w-3.5 text-gold-600" /> Your secret is encrypted at rest and never sent back to the browser.
        </p>
      </Card>

      <Card className="mt-6 max-w-xl rounded-2xl border-navy-900/5 bg-white p-8 shadow-ambient">
        <div className="flex items-center gap-2 text-navy-900">
          <CalendarPlus className="h-5 w-5 text-gold-600" />
          <h3 className="font-serif text-xl font-bold">Booking widget link</h3>
        </div>
        <p className="mt-2 text-sm text-navy-500">
          Paste your GoHighLevel calendar booking link. Clients tap "Book a session" in their dashboard to open it —
          booking in GHL keeps your counsellor-notification workflow firing.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <Label>GHL calendar booking URL</Label>
            <Input data-testid="booking-url-input" value={bookingUrl} onChange={(e) => setBookingUrl(e.target.value)} placeholder="https://api.leadconnectorhq.com/widget/booking/..." className="mt-1 font-mono text-sm focus-visible:ring-gold-500" />
          </div>
          <Button data-testid="booking-url-save" onClick={saveBooking} disabled={savingBooking} className="rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600">
            {savingBooking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save booking link
          </Button>
          <div className="pt-2">
            <Label>Pricing table URL (GHL)</Label>
            <Input data-testid="pricing-url-input" value={pricingUrl} onChange={(e) => setPricingUrl(e.target.value)} placeholder="https://.../preview/..." className="mt-1 font-mono text-sm focus-visible:ring-gold-500" />
            <p className="mt-1 text-xs text-navy-400">Shown in the client's "Sessions & Packages" tab. Leave blank to use the built-in Stripe plans instead.</p>
          </div>
          <Button data-testid="pricing-url-save" onClick={savePricing} disabled={savingPricing} className="rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600">
            {savingPricing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save pricing link
          </Button>
        </div>
      </Card>

      <Card className="mt-6 max-w-xl rounded-2xl border-navy-900/5 bg-white p-8 shadow-ambient" data-testid="dashboard-tiles-card">
        <div className="flex items-center gap-2 text-navy-900">
          <LayoutGrid className="h-5 w-5 text-gold-600" />
          <h3 className="font-serif text-xl font-bold">Dashboard tile images</h3>
        </div>
        <p className="mt-2 text-sm text-navy-500">
          These images appear on the client dashboard cards. Click a tile to replace its image, then save.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-4">
          {TILE_META.map((t) => {
            const preview = tileFiles[t.key] ? URL.createObjectURL(tileFiles[t.key]) : tiles[t.key];
            return (
              <label key={t.key} data-testid={`tile-drop-${t.key}`} className="group cursor-pointer">
                <div className="overflow-hidden rounded-xl border border-navy-100 bg-ivory transition-colors group-hover:border-gold-400">
                  <div className="relative h-24 w-full bg-gold-50">
                    {preview
                      ? <img src={preview} alt={t.label} className="h-full w-full object-cover" />
                      : <div className="flex h-full items-center justify-center"><ImageIcon className="h-6 w-6 text-gold-300" /></div>}
                  </div>
                  <div className="px-3 py-2 text-xs font-medium text-navy-700">{t.label}</div>
                </div>
                <input type="file" accept="image/*" className="hidden" data-testid={`tile-input-${t.key}`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setTileFiles((p) => ({ ...p, [t.key]: f })); }} />
              </label>
            );
          })}
        </div>
        <Button data-testid="tiles-save" onClick={saveTiles} disabled={savingTiles || Object.keys(tileFiles).length === 0} className="mt-4 rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600">
          {savingTiles ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save tile images
        </Button>
      </Card>
    </div>
  );
}
